import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/SophosMSP.css";
import sophosLogo from "../../assets/images/partners/Sophos MSP Partner.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const platformCards = [
  { icon: "🖥️", title: "Sophos Central MSP", desc: "Single-pane-of-glass MSP console managing all customer Sophos environments from one interface — with sub-tenant isolation, customer switching, and MSP-level policy templates deployable across any customer account." },
  { icon: "🔐", title: "Multi-Tenant Security", desc: "Complete security isolation between customer environments — each customer's Sophos Central deployment is independently controlled with separate policies, reporting, and billing while managed from your MSP console." },
  { icon: "📊", title: "MSP Billing & Licensing", desc: "Sophos MSP Flex licensing with monthly true-up billing — aligning security costs to actual customer endpoint counts without upfront commitments, enabling MSPs to grow customer security coverage without financial risk." },
  { icon: "⚡", title: "Automated Customer Provisioning", desc: "New customer environments provisioned in minutes from Sophos Central MSP — automatically applying baseline security policies, enrolling in managed detection, and configuring reporting templates for rapid onboarding." },
  { icon: "📈", title: "Executive Reporting", desc: "Automated security posture reports generated per customer — summarizing threat detections, policy compliance, MDR activity, and security improvements for monthly customer business reviews." },
  { icon: "🔗", title: "RMM & PSA Integration", desc: "Native integrations with ConnectWise Manage, Autotask, Kaseya, NinjaRMM, and Datto — synchronizing Sophos alerts to PSA tickets and Sophos device enrollment to RMM asset inventories automatically." },
];

const tenantData = [
  { name: "Acme Financial LLC", endpoints: "187 devices", status: "Protected", statusCls: "green", avatarCls: "blue", avatarLabel: "AF" },
  { name: "City Medical Group", endpoints: "312 devices", status: "MDR Active", statusCls: "blue", avatarCls: "green", avatarLabel: "CM" },
  { name: "Riverside Manufacturing", endpoints: "94 devices", status: "Alert (2)", statusCls: "orange", avatarCls: "orange", avatarLabel: "RM" },
  { name: "Summit Law Partners", endpoints: "63 devices", status: "Protected", statusCls: "green", avatarCls: "blue", avatarLabel: "SL" },
];

const threatEvents = [
  { sev: "crit", msg: "Ransomware blocked — Acme Financial / PC-ACCOUNTING-04 — 14:32:07" },
  { sev: "med", msg: "Phishing email quarantined — City Medical / user@domain — 14:29:44" },
  { sev: "low", msg: "Malicious web category blocked — Riverside / WS-FLOOR-11 — 14:28:19" },
  { sev: "med", msg: "Lateral movement detected — Sophos MDR investigating — 14:25:01" },
];

const lifecycleSteps = [
  { num: 1, icon: "🤝", title: "Onboard Customer", desc: "Provision customer Sophos Central tenant, apply MSP baseline policy, and enroll endpoints through Sophos deployment scripts." },
  { num: 2, icon: "🛡️", title: "Deploy Protection", desc: "Intercept X agent rolled out via RMM, email security configured, and MDR enrollment activated for managed threat response." },
  { num: 3, icon: "📊", title: "Monitor & Manage", desc: "Continuous monitoring through MSP dashboard — Sophos MDR handles threat investigation, your team manages policy and exceptions." },
  { num: 4, icon: "📈", title: "Review & Grow", desc: "Monthly security review delivered to customer with executive reporting — identifying coverage gaps and expansion opportunities." },
];

const mdrFeatures = [
  { icon: "🔭", title: "Continuous Threat Hunting", desc: "Sophos MDR analysts actively hunt threats in every managed customer environment — going beyond automated detection to investigate behavioral indicators and low-confidence signals that rule-based systems miss." },
  { icon: "⚡", title: "24/7 Incident Response", desc: "When Sophos MDR detects an active threat in any customer environment, analysts immediately investigate and contain — providing MSPs with incident response capability without requiring dedicated security operations staff." },
  { icon: "📋", title: "Customer-Level Reporting", desc: "MDR activity reports per customer — summarizing threats investigated, incidents contained, hunting activities, and security improvements identified during the period for MSP customer business reviews." },
  { icon: "🔗", title: "MSP Alert Workflow Integration", desc: "Sophos MDR creates tickets in your PSA platform for significant security events — with full analyst context, recommended customer communications, and response actions already documented." },
];

const autoFlows = [
  { icon: "🤖", title: "Policy Template Deployment", desc: "MSP-level security policy templates pushed to new customer environments automatically on tenant creation — eliminating manual policy configuration for each new customer onboarded." },
  { icon: "📥", title: "Endpoint Auto-Enrollment", desc: "Sophos deployment scripts distributed through RMM automatically enroll new customer devices in Sophos Central — maintaining 100% endpoint coverage without manual MSP action for each device added." },
  { icon: "🎫", title: "Alert-to-Ticket Automation", desc: "Sophos Central alerts automatically create ConnectWise or Autotask tickets — with asset, customer, severity, and recommended action pre-populated, eliminating manual alert review and ticket creation." },
  { icon: "📊", title: "Monthly Report Generation", desc: "Executive security reports automatically generated and emailed to customer contacts each month — summarizing protection activity, threats blocked, MDR incidents, and security posture score without MSP manual effort." },
  { icon: "💰", title: "Usage-Based Billing Sync", desc: "Sophos Central MSP endpoint counts synchronized to your billing system monthly — automatically calculating customer invoices based on actual device counts without manual license reconciliation." },
];

const growthCards = [
  { icon: "💰", title: "Recurring Security Revenue", desc: "Sophos MSP Flex creates a predictable monthly revenue stream per endpoint — turning one-time security product sales into ongoing managed service revenue with strong customer retention." },
  { icon: "🤝", title: "Sophos Partner Program", desc: "MSP tier benefits including marketing development funds, technical training resources, certified partner badging, and co-branded customer security assessments to support your sales pipeline." },
  { icon: "📈", title: "Upsell to MDR", desc: "Starting customers on Intercept X endpoint protection and upselling Sophos MDR provides a clear growth path — MDR delivers significantly higher recurring revenue and stronger customer dependency." },
  { icon: "🛠️", title: "Pre-Sales Engineering", desc: "ALG Data Guard provides pre-sales engineering support for Sophos MSP opportunities — designing multi-customer Sophos architectures and scoping MDR service inclusions for customer proposals." },
  { icon: "🎓", title: "MSP Enablement", desc: "Sophos technical training and ALG Data Guard onboarding assistance ensuring your team can efficiently manage Sophos Central MSP, respond to customer security events, and conduct security business reviews." },
  { icon: "🔄", title: "Customer Retention", desc: "MSP customers with Sophos MDR have significantly higher retention rates — security operations dependency creates genuine switching cost, reducing churn and improving customer lifetime value." },
];

const endpointStack = [
  { icon: "🛡️", title: "Intercept X Advanced with MDR", desc: "Premium endpoint protection combining deep learning malware detection, behavioral analysis, exploit prevention, CryptoGuard ransomware rollback, and Sophos MDR managed threat response — delivering enterprise-grade security from a single agent." },
  { icon: "🔄", title: "Synchronized Security for MSPs", desc: "Sophos Firewall and Intercept X share threat intelligence through the Security Heartbeat — enabling MSPs to sell integrated network and endpoint security that automatically isolates compromised customer endpoints." },
  { icon: "📧", title: "Sophos Email for MSPs", desc: "Multi-tenant email security managed from Sophos Central MSP — providing customer-specific anti-spam, anti-phishing, and data loss prevention policies from the same console as endpoint and network security." },
  { icon: "📱", title: "Sophos Mobile MDM", desc: "Mobile device management integrated into Sophos Central MSP — managing customer iOS, Android, and Windows Mobile device security policies from the same platform as endpoint protection." },
];

const industries = [
  { icon: "🏢", name: "SMB Enterprise", desc: "Complete managed security platform for small to mid-market businesses — endpoint, email, and MDR delivered as a monthly managed service with enterprise-grade capabilities." },
  { icon: "🏥", name: "Healthcare SMB", desc: "HIPAA-compliant managed security for medical practices, dental offices, and outpatient clinics — with MDR coverage specifically valuable for healthcare ransomware threat landscape." },
  { icon: "⚖️", name: "Legal Professionals", desc: "Client confidentiality protection, privileged document security, and managed endpoint protection for law firms requiring consistent security without dedicated IT staff." },
  { icon: "🏦", name: "Financial Advisors", desc: "SEC/FINRA-aligned managed security for RIAs and wealth management firms — endpoint protection, email security, and MDR meeting regulatory cybersecurity requirements." },
  { icon: "🏛️", name: "Non-Profit", desc: "Affordable managed security services for non-profit organizations — Sophos MSP pricing enables non-profits to access enterprise-quality protection within constrained IT budgets." },
  { icon: "🏭", name: "Light Manufacturing", desc: "Managed endpoint security for manufacturing SMBs — protecting corporate IT environments adjacent to OT/production floor systems with MDR coverage for ransomware threats." },
  { icon: "🎓", name: "K-12 Education", desc: "Managed security for private schools and charter schools — CIPA-compliant web filtering, endpoint protection, and MDR for districts without dedicated cybersecurity staff." },
  { icon: "🔬", name: "Professional Services", desc: "Managed security for consulting firms, engineering practices, and professional services — protecting client data and intellectual property through comprehensive managed endpoint and email security." },
];

const faqData = [
  { q: "What is Sophos Central MSP and how does it enable managed security services?", a: "Sophos Central MSP is a dedicated management console designed for managed service providers — providing a master console that manages multiple customer Sophos Central environments from a single interface. Unlike enterprise Sophos Central (which manages one organization), Sophos Central MSP allows MSP engineers to switch between customer tenants, view all customer security status in a consolidated dashboard, push policy templates across customer accounts, generate per-customer reports, and manage licensing through a unified billing model. Key MSP capabilities: Customer tenant switching without logging out — engineers manage all customers from one session. Consolidated alert view — seeing high-severity events across all customer environments in priority order. Policy template management — creating standardized security baselines deployable to new customers automatically. MSP-level reporting — generating customer-facing security reports without building them manually. Sophos MSP Flex licensing — true-up monthly billing aligned to actual customer endpoint counts, eliminating per-customer license management complexity." },
  { q: "What is Sophos MDR and how should MSPs position it to customers?", a: "Sophos MDR (Managed Detection and Response) is a 24/7 security operations service where Sophos expert analysts monitor customer environments, proactively hunt for threats, investigate suspicious activity, and respond to incidents. For MSPs, Sophos MDR provides the security operations capability that most MSPs cannot build in-house — dedicated threat hunters with specialized expertise monitoring customer environments around the clock, responding to active incidents immediately regardless of time zone or day. MSP positioning: Customers who have experienced a security incident or are concerned about ransomware understand the value of 24/7 expert monitoring. MDR is best positioned as the premium security tier for customers who cannot afford a breach — healthcare, legal, financial services, and regulated industries. The key customer message is that MDR means a team of expert security analysts is actively monitoring their environment every hour of every day, not just automated alerts. MSPs retain significant value by managing the relationship, delivering monthly business reviews, and handling Sophos MDR coordination while Sophos analysts handle the technical security operations." },
  { q: "How does Sophos MSP Flex licensing work?", a: "Sophos MSP Flex is Sophos's subscription licensing model designed for managed service providers — providing monthly billing based on actual endpoint counts across all customer environments without annual commitments or upfront seat purchases. Monthly true-up: MSPs report actual endpoint counts for each customer monthly, paying only for active protected devices. Growing customers automatically add to monthly billing; shrinking customers reduce billing without penalty. Product flexibility: MSP Flex covers Intercept X (all tiers), Sophos MDR, Sophos Email, Sophos Mobile, and Sophos Firewall. MSPs bundle these into customer service packages at their own pricing — marking up Sophos cost to build managed service margin. Customer billing: MSPs typically bill customers monthly per-endpoint or per-user for 'managed security services' — the Sophos MSP Flex cost is embedded in the MSP's service price rather than passed through separately. No minimum commitments: Unlike traditional software licensing requiring annual seat counts upfront, Sophos MSP Flex allows MSPs to start small and grow without financial risk from committing to licenses before customer enrollment." },
  { q: "What PSA and RMM integrations does Sophos provide for MSPs?", a: "Sophos provides native integration with the major MSP toolchain platforms: ConnectWise Manage — Sophos Central alerts automatically create ConnectWise service tickets with customer, asset, severity, and recommended action pre-populated. Sophos endpoint status syncs to ConnectWise asset records. Autotask PSA — similar alert-to-ticket automation with Autotask ticket creation and Sophos asset sync to Autotask configuration items. Kaseya VSA — endpoint deployment through Kaseya scripting and Sophos alert integration into Kaseya service desk. NinjaRMM — Sophos Central integration for device enrollment and alert visibility within the Ninja console. Datto RMM — Sophos deployment automation and alert workflow integration. ConnectWise Automate — scripted Sophos deployment and Sophos alert to Automate ticket integration. Beyond PSA integrations, Sophos provides IT Glue documentation sync for keeping customer Sophos configuration documentation current. ALG Data Guard helps MSPs configure these integrations during Sophos MSP onboarding — ensuring alert-to-ticket automation is working correctly before the first customer is onboarded." },
  { q: "How does ALG Data Guard support MSPs deploying Sophos?", a: "ALG Data Guard provides comprehensive MSP enablement for Sophos deployment: Initial onboarding: Sophos Central MSP tenant configuration, MSP policy template design, and integration setup for your PSA and RMM platforms — ensuring your MSP console is operational before your first customer is onboarded. First customer deployment: Hands-on support for your first 1-3 Sophos customer deployments — walking your team through the enrollment process, policy application, MDR activation, and reporting setup to build team confidence. Technical training: Sophos product training covering Intercept X advanced features, MDR coordination workflows, Sophos Central MSP administration, and customer escalation procedures. Customer growth support: Pre-sales engineering for new Sophos MSP customer opportunities — designing appropriate Sophos configurations and MDR service inclusions for customer proposals. Ongoing support: Sophos technical questions, configuration assistance, MDR escalation coordination, and quarterly Sophos platform review to ensure your MSP deployment remains optimized as your customer base grows. ALG Data Guard is your Sophos technical resource, enabling your MSP to offer Sophos managed security without building deep Sophos expertise internally." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function SophosMSP() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(500, 2200, metricsInView);
  const c2 = useCounter(40, 2000, metricsInView);
  const c3 = useCounter(24, 2200, metricsInView);
  const c4 = useCounter(99, 2000, metricsInView);

  return (
    <div className="sm-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={sophosLogo} alt="Sophos MSP" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Managed Security Services Provider Platform Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Sophos MSP</h1>
            <p className="pd-identity-desc">
              Sophos Managed Security Services is a platform purpose-built for Managed Service Providers — delivering multi-tenant security management, 24/7 Managed Detection and Response, endpoint protection, email security, and automated MSP operations through Sophos Central MSP. Where consumer-grade security tools create operational complexity across customer environments, Sophos Central MSP provides MSPs with a unified console, automated provisioning, PSA integration, and MSP Flex licensing enabling profitable, scalable managed security services. ALG Data Guard is an authorized Sophos MSP partner providing enablement, deployment, and technical support for MSPs building managed security practices on the Sophos platform.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="sm-hero">
        <div className="sm-hero__bg" aria-hidden="true" />
        <div className="sm-hero__grid" aria-hidden="true" />
        <div className="sm-hero__inner">
          <div>
            <div className="sm-tag sm-tag--green"><span className="sm-dot" /> Sophos MSP Authorized Partner</div>
            <h2 className="sm-hero__title">
              Managed Security.<br /><em>Delivered at Scale.</em>
            </h2>
            <p className="sm-hero__body">
              Sophos Central MSP gives your managed services practice a single console, automated provisioning, and 24/7 Sophos MDR — transforming endpoint security from a vendor relationship into a scalable, profitable recurring revenue service. Protect every customer. Manage from one place. Grow without limits.
            </p>
            <div className="sm-hero__actions">
              <button className="sm-btn sm-btn--blue" onClick={() => navigate("/contact")}>
                Launch My MSP Practice <ArrowIcon />
              </button>
              <button className="sm-btn sm-btn--ghost" onClick={() => navigate("/contact")}>
                MSP Enablement
              </button>
            </div>
            <div className="sm-hero__proof">
              <div><div className="sm-hero__proof-val">500M+</div><div className="sm-hero__proof-label">Endpoints Protected</div></div>
              <div><div className="sm-hero__proof-val">24/7</div><div className="sm-hero__proof-label">MDR Coverage</div></div>
              <div><div className="sm-hero__proof-val">Multi-</div><div className="sm-hero__proof-label">Tenant Console</div></div>
            </div>
          </div>

          {/* MSP Dashboard mock */}
          <div className="sm-dashboard">
            <div className="sm-dash-header">
              <span className="sm-dash-title">Sophos Central MSP — All Customers</span>
              <div className="sm-dash-controls">
                <div className="sm-dash-dot sm-dash-dot--r" />
                <div className="sm-dash-dot sm-dash-dot--y" />
                <div className="sm-dash-dot sm-dash-dot--g" />
              </div>
            </div>
            <div className="sm-kpi-row">
              <div className="sm-kpi-box"><div className="sm-kpi-box__val sm-kpi-box__val--green">1,247</div><div className="sm-kpi-box__label">Endpoints</div></div>
              <div className="sm-kpi-box"><div className="sm-kpi-box__val sm-kpi-box__val--blue">28</div><div className="sm-kpi-box__label">Customers</div></div>
              <div className="sm-kpi-box"><div className="sm-kpi-box__val sm-kpi-box__val--orange">3</div><div className="sm-kpi-box__label">Alerts</div></div>
            </div>
            <div className="sm-customer-list">
              {tenantData.map(t => (
                <div key={t.name} className="sm-cust-row">
                  <div className={`sm-cust-avatar sm-cust-avatar--${t.avatarCls}`}>{t.avatarLabel}</div>
                  <span className="sm-cust-name">{t.name}</span>
                  <span className="sm-cust-devices">{t.endpoints}</span>
                  <span className={`sm-status sm-status--${t.statusCls}`}>
                    <span className="sm-status-dot" />{t.status}
                  </span>
                </div>
              ))}
            </div>
            <div className="sm-threat-feed">
              <div style={{ fontSize:"0.62rem", color:"rgba(255,255,255,0.3)", fontFamily:"IBM Plex Mono", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:"0.2rem" }}>Live Threat Feed</div>
              {threatEvents.map((e, i) => (
                <div key={i} className="sm-threat-event">
                  <div className={`sm-threat-sev sm-threat-sev--${e.sev}`} />
                  <span>{e.msg}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── MSP Platform ── */}
      <section className="sm-platform">
        <div className="sm-shell">
          <div className="sm-platform__head">
            <div className="sm-tag sm-tag--blue"><span className="sm-dot" /> MSP Platform</div>
            <h2 className="sm-title" style={{ color:"var(--sm-text)" }}>One Console to Protect Every Customer</h2>
            <p className="sm-sub" style={{ margin:"0 auto", maxWidth:"600px", color:"var(--sm-muted)" }}>Sophos Central MSP is built specifically for the operational reality of managed security — multi-tenant isolation, automated provisioning, usage-based billing, and deep PSA integration enabling profitable, scalable security services.</p>
          </div>
          <div className="sm-platform__grid">
            {platformCards.map(card => (
              <div key={card.title} className="sm-plat-card">
                <span className="sm-plat-card__icon">{card.icon}</span>
                <h3 className="sm-plat-card__title">{card.title}</h3>
                <p className="sm-plat-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Multi-Tenant ── */}
      <section className="sm-multitenant">
        <div className="sm-shell">
          <div className="sm-multitenant__inner">
            <div>
              <div className="sm-tag sm-tag--dark"><span className="sm-dot" /> Multi-Tenant Management</div>
              <h2 className="sm-title sm-multitenant__title">Manage Every Customer. Isolate Every Environment.</h2>
              <p className="sm-multitenant__body">
                Sophos Central MSP provides complete tenant isolation — each customer operates in their own Sophos Central environment with separate policies, separate reporting, and separate billing, while your MSP console has visibility and control across all customer tenants from a single authenticated session.
              </p>
              <p className="sm-multitenant__body">
                MSP engineers switch between customer environments instantly without re-authenticating — viewing customer-specific threat alerts, policy compliance status, and endpoint health. MSP-level reporting summarizes security posture across your entire book of business for business review conversations.
              </p>
              <div className="sm-tenant-list">
                {tenantData.map(t => (
                  <div key={t.name} className="sm-tenant-item">
                    <span className="sm-tenant-icon">🏢</span>
                    <span className="sm-tenant-name">{t.name}</span>
                    <div className="sm-tenant-info">
                      <span className="sm-tenant-count">{t.endpoints}</span>
                      <span className={`sm-status sm-status--${t.statusCls}`}><span className="sm-status-dot" />{t.status}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="sm-btn sm-btn--blue" onClick={() => navigate("/contact")}>
                  Set Up My MSP Console <ArrowIcon />
                </button>
              </div>
            </div>

            {/* Multi-tenant panel mock */}
            <div className="sm-mt-panel">
              <div className="sm-mt-panel__bar">
                <span className="sm-mt-panel__tab sm-mt-panel__tab--active">All Customers</span>
                <span className="sm-mt-panel__tab">Alerts</span>
                <span className="sm-mt-panel__tab">Reports</span>
                <span className="sm-mt-panel__tab">Billing</span>
              </div>
              <div className="sm-mt-panel__body">
                <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 70px", padding:"0.4rem 0.9rem", fontSize:"0.65rem", color:"rgba(255,255,255,0.3)", fontFamily:"IBM Plex Mono" }}>
                  <span>CUSTOMER</span><span style={{textAlign:"center"}}>ENDPOINTS</span><span style={{textAlign:"right"}}>STATUS</span>
                </div>
                {[
                  { name:"Acme Financial", ep:"187", st:"green", sv:"Protected" },
                  { name:"City Medical Group", ep:"312", st:"blue", sv:"MDR Active" },
                  { name:"Riverside Mfg", ep:"94", st:"orange", sv:"Alert" },
                  { name:"Summit Law", ep:"63", st:"green", sv:"Protected" },
                  { name:"Harbor Retail", ep:"156", st:"green", sv:"Protected" },
                  { name:"Apex Consulting", ep:"48", st:"blue", sv:"MDR Active" },
                ].map(r => (
                  <div key={r.name} className="sm-mt-row">
                    <span className="sm-mt-name">{r.name}</span>
                    <span className="sm-mt-endpoints">{r.ep}</span>
                    <div style={{ display:"flex", justifyContent:"flex-end" }}>
                      <span className={`sm-status sm-status--${r.st}`}><span className="sm-status-dot" />{r.sv}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="sm-mt-panel__footer">
                <span>Showing 6 of 28 customers</span>
                <span>Total: 1,247 endpoints</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Customer Lifecycle ── */}
      <section className="sm-lifecycle">
        <div className="sm-shell">
          <div className="sm-lifecycle__head">
            <div className="sm-tag sm-tag--blue"><span className="sm-dot" /> Customer Lifecycle</div>
            <h2 className="sm-title" style={{ color:"var(--sm-text)" }}>From Prospect to Protected — In Hours, Not Days</h2>
            <p className="sm-sub" style={{ margin:"0 auto", maxWidth:"580px", color:"var(--sm-muted)" }}>Sophos MSP automation compresses the customer onboarding timeline dramatically — with Sophos Central tenant provisioning, policy deployment, and endpoint enrollment happening automatically from a single workflow trigger.</p>
          </div>
          <div className="sm-lifecycle__pipeline">
            {lifecycleSteps.map(step => (
              <div key={step.num} className="sm-lc-step">
                <div className="sm-lc-step__num">{step.num}</div>
                <span className="sm-lc-step__icon">{step.icon}</span>
                <h3 className="sm-lc-step__title">{step.title}</h3>
                <p className="sm-lc-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MDR ── */}
      <section className="sm-mdr">
        <div className="sm-shell">
          <div className="sm-mdr__inner">
            <img
              src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=900&q=80"
              alt="Sophos MDR security operations"
              className="sm-mdr__img"
            />
            <div>
              <div className="sm-tag sm-tag--dark"><span className="sm-dot" /> Managed Detection & Response</div>
              <h2 className="sm-title sm-mdr__title">24/7 Expert Security Operations — For Every Customer</h2>
              <p className="sm-mdr__body">
                Sophos MDR is the most widely adopted MDR service globally — providing 24/7 threat hunting, investigation, and incident response for every customer environment in your MSP portfolio. For MSPs, Sophos MDR eliminates the operational burden of security operations while creating a premium managed security tier that generates significantly higher recurring revenue than endpoint-only managed security.
              </p>
              <p className="sm-mdr__body">
                Sophos MDR analysts work in coordination with your MSP team — Sophos handles technical threat response while you maintain the customer relationship, deliver business reviews, and manage policy exceptions. The result is enterprise-quality security operations delivered through your MSP practice without building a dedicated SOC.
              </p>
              <div className="sm-mdr__features">
                {mdrFeatures.map(f => (
                  <div key={f.title} className="sm-mdr-feat">
                    <span className="sm-mdr-feat__icon">{f.icon}</span>
                    <div>
                      <div className="sm-mdr-feat__title">{f.title}</div>
                      <p className="sm-mdr-feat__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="sm-btn sm-btn--green" onClick={() => navigate("/contact")}>
                  Add MDR to My MSP <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Automation ── */}
      <section className="sm-automation">
        <div className="sm-shell">
          <div className="sm-automation__inner">
            <div>
              <div className="sm-tag sm-tag--blue"><span className="sm-dot" /> MSP Automation</div>
              <h2 className="sm-title sm-automation__title" style={{ color:"var(--sm-text)" }}>Eliminate Manual Security Operations Work</h2>
              <p className="sm-automation__body">
                The operational challenge of managed security isn't the security technology — it's the operational overhead of managing alerts, provisioning new customers, maintaining endpoint coverage, and producing customer reports across dozens of customer environments. Sophos Central MSP automation eliminates this overhead systematically.
              </p>
              <p className="sm-automation__body">
                From automated endpoint enrollment through alert-to-ticket PSA integration through monthly report generation — Sophos MSP automation ensures your team spends time on customer relationships and strategic security, not manual operations work that should be automatic.
              </p>
              <div className="sm-automation__flows">
                {autoFlows.map(f => (
                  <div key={f.title} className="sm-auto-flow">
                    <span className="sm-auto-flow__icon">{f.icon}</span>
                    <div>
                      <div className="sm-auto-flow__title">{f.title}</div>
                      <p className="sm-auto-flow__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80"
              alt="Security automation operations"
              className="sm-automation__img"
            />
          </div>
        </div>
      </section>

      {/* ── Partner Growth ── */}
      <section className="sm-growth">
        <div className="sm-shell">
          <div className="sm-growth__head">
            <div className="sm-tag sm-tag--dark"><span className="sm-dot" /> Partner Growth</div>
            <h2 className="sm-title">Build a Profitable Managed Security Practice</h2>
            <p className="sm-sub" style={{ margin:"0 auto", maxWidth:"600px" }}>Sophos MSP isn't just a security product — it's a business model. Sophos's MSP program, flexible licensing, and partner enablement are designed to make managed security a growth driver for your MSP practice.</p>
          </div>
          <div className="sm-growth__grid">
            {growthCards.map(card => (
              <div key={card.title} className="sm-growth-card">
                <span className="sm-growth-card__icon">{card.icon}</span>
                <h3 className="sm-growth-card__title">{card.title}</h3>
                <p className="sm-growth-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:"3rem", position:"relative", zIndex:2 }}>
            <button className="sm-btn sm-btn--blue" onClick={() => navigate("/contact")}>
              Build My MSP Security Practice <ArrowIcon />
            </button>
          </div>
        </div>
      </section>

      {/* ── Endpoint ── */}
      <section className="sm-endpoint">
        <div className="sm-shell">
          <div className="sm-endpoint__inner">
            <img
              src="https://images.unsplash.com/photo-1563206767-5b18f218e8de?auto=format&fit=crop&w=900&q=80"
              alt="Sophos endpoint protection for MSPs"
              className="sm-endpoint__img"
            />
            <div>
              <div className="sm-tag sm-tag--blue"><span className="sm-dot" /> Endpoint Protection</div>
              <h2 className="sm-title sm-endpoint__title" style={{ color:"var(--sm-text)" }}>Premium Endpoint Security. Delivered as a Service.</h2>
              <p className="sm-endpoint__body">
                Sophos Intercept X is the foundation of every Sophos MSP customer deployment — providing enterprise-grade endpoint protection that consistently top-ranks in independent evaluations without requiring complex management expertise from your MSP team. Sophos Central MSP handles policy management, reporting, and alerts; your team delivers the customer relationship.
              </p>
              <div className="sm-endpoint__stack">
                {endpointStack.map(item => (
                  <div key={item.title} className="sm-ep-item">
                    <span className="sm-ep-item__icon">{item.icon}</span>
                    <div>
                      <div className="sm-ep-item__title">{item.title}</div>
                      <p className="sm-ep-item__desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="sm-btn sm-btn--blue" onClick={() => navigate("/contact")}>
                  Deploy Intercept X for Customers <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="sm-industries">
        <div className="sm-shell">
          <div className="sm-industries__head">
            <div className="sm-tag sm-tag--blue"><span className="sm-dot" /> Customer Industries</div>
            <h2 className="sm-title" style={{ color:"var(--sm-text)" }}>Managed Security for Every SMB Vertical</h2>
            <p className="sm-sub" style={{ margin:"0 auto", maxWidth:"560px", color:"var(--sm-muted)" }}>Sophos MSP serves every SMB market vertical — with industry-specific compliance configurations and threat profiles relevant to each customer segment.</p>
          </div>
          <div className="sm-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="sm-ind-card">
                <span className="sm-ind-card__icon">{ind.icon}</span>
                <h3 className="sm-ind-card__name">{ind.name}</h3>
                <p className="sm-ind-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="sm-metrics" ref={metricsRef}>
        <div className="sm-shell">
          <div className="sm-metrics__inner">
            <div><div className="sm-metric__val">{c1}M+</div><div className="sm-metric__label">Endpoints on Sophos Platform</div></div>
            <div><div className="sm-metric__val">{c2}%</div><div className="sm-metric__label">Reduction in MSP Alert Noise</div></div>
            <div><div className="sm-metric__val">{c3}/7</div><div className="sm-metric__label">MDR Coverage Hours</div></div>
            <div><div className="sm-metric__val">{c4}%</div><div className="sm-metric__label">Threat Detection Rate</div></div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="sm-faq">
        <div className="sm-shell">
          <div className="sm-faq__head">
            <div className="sm-tag sm-tag--blue" style={{ margin:"0 auto 1rem" }}><span className="sm-dot" /> FAQs</div>
            <h2 className="sm-title" style={{ color:"var(--sm-text)" }}>Sophos MSP Questions, Answered</h2>
            <p className="sm-sub" style={{ margin:"0 auto", maxWidth:"560px", color:"var(--sm-muted)" }}>Expert answers from ALG Data Guard's Sophos MSP specialists about Central MSP, MDR, Flex licensing, and PSA integrations.</p>
          </div>
          <div className="sm-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`sm-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="sm-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="sm-faq-item__q">{faq.q}</span>
                  <span className="sm-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="sm-faq-item__answer"><div className="sm-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="sm-cta">
        <div className="sm-cta__inner">
          <div className="sm-tag sm-tag--green" style={{ margin:"0 auto 1.2rem" }}><span className="sm-dot" /> Get Started</div>
          <h2 className="sm-cta__title">
            Launch Your Managed Security<br />Practice with <span>Sophos.</span>
          </h2>
          <p className="sm-cta__sub">Whether you're adding managed security to an existing MSP practice or building a dedicated cybersecurity service offering from the ground up — ALG Data Guard provides the Sophos enablement, technical support, and onboarding assistance to make your managed security practice operational, profitable, and scalable.</p>
          <div className="sm-cta__actions">
            <button className="sm-btn sm-btn--blue" onClick={() => navigate("/contact")}>
              Launch My MSP Practice <ArrowIcon />
            </button>
            <button className="sm-btn sm-btn--white" onClick={() => navigate("/contact")}>
              MSP Enablement Session
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
